import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, RefreshCw, UserPlus, Trash2 } from 'lucide-react';

interface BoardMember {
  id: string;
  full_name: string | null;
  user_id: string;
  role: 'CHAIR' | 'DIRECTOR' | 'OBSERVER';
  active: boolean;
  created_at: string;
}

interface BoardRosterToolProps {
  companyId: string;
}

export function BoardRosterTool({ companyId }: BoardRosterToolProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'CHAIR' | 'DIRECTOR' | 'OBSERVER'>('DIRECTOR');
  const [members, setMembers] = useState<BoardMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  async function loadMembers() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('board_members')
        .select('id, full_name, user_id, role, active, created_at')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setMembers((data || []) as BoardMember[]);
    } catch (err) {
      console.error('Error loading board members:', err);
      toast.error('Failed to load board members');
    } finally {
      setLoading(false);
    }
  }

  async function addMember() {
    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }

    try {
      setAdding(true);
      toast.info('Searching for user...');

      // Get user by email using RPC function
      const { data: userData, error: userError } = await supabase.rpc('get_user_by_email', {
        p_email: email.trim(),
      });

      if (userError) throw userError;

      if (!userData || userData.length === 0) {
        throw new Error('User not found. They may need to sign up first.');
      }

      const userId = userData[0]?.id;
      if (!userId) {
        throw new Error('Invalid user data');
      }

      // Check if already a board member
      const { data: existing } = await supabase
        .from('board_members')
        .select('id')
        .eq('company_id', companyId)
        .eq('user_id', userId)
        .maybeSingle();

      if (existing) {
        throw new Error('User is already a board member');
      }

      // Add board member
      const { error: insertError } = await supabase.from('board_members').insert({
        company_id: companyId,
        user_id: userId,
        full_name: name.trim() || userData[0]?.email || null,
        role,
        active: true,
      });

      if (insertError) throw insertError;

      toast.success('Board member added successfully');
      setEmail('');
      setName('');
      setRole('DIRECTOR');
      loadMembers();
    } catch (err: any) {
      console.error('Error adding board member:', err);
      toast.error(err.message || 'Failed to add board member');
    } finally {
      setAdding(false);
    }
  }

  async function removeMember(id: string, memberName: string | null) {
    if (!confirm(`Are you sure you want to deactivate ${memberName || 'this member'}?`)) {
      return;
    }

    try {
      const { error } = await supabase.from('board_members').update({ active: false }).eq('id', id);

      if (error) throw error;

      toast.success('Member deactivated');
      loadMembers();
    } catch (err: any) {
      console.error('Error removing board member:', err);
      toast.error(err.message || 'Failed to remove member');
    }
  }

  useEffect(() => {
    if (companyId) {
      loadMembers();
    }
  }, [companyId]);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg font-semibold">Board Roster</CardTitle>
        <Button variant="outline" size="sm" onClick={loadMembers}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add Member Form */}
        <div className="grid gap-4 p-4 border rounded-lg bg-muted/50">
          <h3 className="text-sm font-medium">Add Board Member</h3>
          <div className="grid sm:grid-cols-4 gap-3">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="director@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Display Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={role} onValueChange={(value: any) => setRole(value)}>
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CHAIR">Chair</SelectItem>
                  <SelectItem value="DIRECTOR">Director</SelectItem>
                  <SelectItem value="OBSERVER">Observer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={addMember} disabled={adding} className="w-full">
                {adding ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" /> Add Member
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Members Table */}
        {members.length > 0 ? (
          <div className="overflow-x-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="font-medium">
                      {member.full_name || member.user_id.slice(0, 8)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{member.role}</Badge>
                    </TableCell>
                    <TableCell>
                      {member.active ? (
                        <Badge className="bg-green-500">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>{new Date(member.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      {member.active && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => removeMember(member.id, member.full_name)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" /> Remove
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center p-8 border rounded-lg bg-muted/50">
            <p className="text-sm text-muted-foreground">
              No board members yet. Add your first member to get started.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
